import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/Layout/PageLayout';
import ContentBlock from '@/components/molecules/ContentBlock';
import type { DateRange } from '@/components/molecules/DateRangePicker/DateRangePicker';
import { EmptyState } from '@/components/molecules/EmptyState';
import DownloadOptions from '@/components/molecules/ExportDataDropDown/DownloadOptions';
import { ExportDataDropDown } from '@/components/molecules/ExportDataDropDown/ExportDataDropDown';
import { LiceReportFilters, LiceReportModeToggle } from '@/components/organisms/LiceReportControls';
import {
    LiceReportLocationTable,
    type LiceReportLocationTableProps,
} from '@/components/organisms/LiceReportLocationTable';
import LiceReportSkeleton from '@/components/organisms/LiceReportSkeleton/LiceReportSkeleton';
import { useAuth, usePermissions } from '@/contexts/auth';
import {
    getDailyLiceReportPeriodLabel,
    isInterimLiceReportWeek,
    type LiceReportDataSource,
    type LiceReportResult,
    type LiceReportWeekNumber,
    type LocationsIncluded,
    mapLiceReportCagesByDataSource,
    mapLiceReportExportColumns,
    toLiceReportWeekOptions,
    useLiceReportSelection,
} from '@/routes/Client/LiceReport';
import RegionPreparePDFdata from '@/routes/Client/Overview/RegionLiceReport/PdfReport/RegionPrepareDataForPDFGeneration';
import calculateAverage from '@/utils/calculateCageAverages';
import { formatDate } from '@/utils/date';

import styles from './LocationLiceReport.module.scss';

export type { LocationsIncluded };

interface LocationLiceReportPageProps {
    clientId: number;
    clientName: string;
    /** Shown below the report, so it does not insert above the filters. */
    notice?: ReactNode;
    locationId: number;
    locationName: string;
    reports: LiceReportResult[];
    weeklyReports: LiceReportResult[];
    isChileanLice?: boolean;
    mode: 'weekly' | 'daily';
    isDailyLiceReportEnabled?: boolean;
    selectedDate: string;
    dataSourceType: LiceReportDataSource;
    dailySubMode: 'single' | 'range';
    rangeFrom: string;
    rangeTo: string;
    isReportLoading?: boolean;
    onModeChange: (mode: 'weekly' | 'daily') => void;
    onDataSourceChange: (dataSourceType: LiceReportDataSource) => void;
    onDateChange: (date: string) => void;
    onDailySubModeChange: (mode: 'single' | 'range') => void;
    onRangeChange: (range: DateRange) => void;
    onWeeklySelectionChange?: (weekNumbers: LiceReportWeekNumber[]) => void;
    /** Week the chips select until the reader picks one. */
    defaultWeekKey?: string;
}

export default function LocationLiceReportPageContent({
    reports,
    weeklyReports,
    clientId,
    clientName,
    notice,
    locationId,
    locationName,
    isChileanLice = false,
    mode,
    isDailyLiceReportEnabled = false,
    selectedDate,
    dataSourceType,
    dailySubMode,
    rangeFrom,
    rangeTo,
    isReportLoading = false,
    onModeChange,
    onDataSourceChange,
    onDateChange,
    onDailySubModeChange,
    onRangeChange,
    onWeeklySelectionChange,
    defaultWeekKey,
}: LocationLiceReportPageProps) {
    const { t, i18n } = useTranslation();
    const { t: tLiceReport } = useTranslation('liceReport');
    const { clients } = useAuth();
    const { isOptoScaleAdmin } = usePermissions();

    const clientLocations = useMemo(
        () => clients.find((c) => c.id === clientId)?.locations ?? [],
        [clients, clientId]
    );

    /**
     * Memoised: the selector treats a new array as a new set of weeks, and rebuilding it inline
     * on every render is what used to reset the reader's selection.
     */
    const weekOptions = useMemo(() => toLiceReportWeekOptions(weeklyReports), [weeklyReports]);

    const { selectedReports, noData, handleCageSelected, onConfigurationChangeHandler } =
        useLiceReportSelection({
            reports,
            weeklyReports,
            mode,
            dataSourceType,
            clientLocations,
            locationId,
            onDataSourceChange,
            onWeeklySelectionChange,
        });

    const prepareTitle = (report: LiceReportResult) => {
        if (report.from && report.to) {
            return `${formatDate(report.from, 'displayLong')} - ${formatDate(
                report.to,
                'displayLong'
            )}`;
        }
        if (report.day) {
            return formatDate(report.day, 'displayLong');
        }
        const isCurrentWeek = isInterimLiceReportWeek(report);
        return `${t('common.labels.week')} ${report.isoWeek} ${report.isoYear} ${
            isCurrentWeek ? `(${t('common.labels.interimReport')})` : ''
        }`;
    };

    const getLiceReportPrefix = (report: LiceReportResult) => {
        const suffix =
            report.from && report.to
                ? `${report.from}-${report.to}`
                : report.day
                  ? report.day
                  : `${t('common.labels.week')}-${report.isoWeek}`;
        if (clientName && locationName) {
            return `${clientName}-${locationName}-lice-report-${suffix}`;
        }
        if (clientName) {
            return `${clientName}-${locationId}-lice-report-${suffix}`;
        }
        return `lice-report-${suffix}`;
    };

    const showDataSourceToggle = isOptoScaleAdmin || !isChileanLice;
    const showFilters = isReportLoading || weeklyReports.length > 0 || isDailyLiceReportEnabled;
    const showSkeleton = isReportLoading || (selectedReports.length === 0 && !noData);

    return (
        <PageLayout
            title={t('common.labels.liceReport')}
            actions={
                isDailyLiceReportEnabled && (
                    <LiceReportModeToggle mode={mode} onModeChange={onModeChange} />
                )
            }>
            {showFilters && (
                <LiceReportFilters
                    mode={mode}
                    weekNumberObjects={weekOptions}
                    onReportConfigurationChange={onConfigurationChangeHandler}
                    dataSourceType={dataSourceType}
                    showDataSourceToggle={showDataSourceToggle}
                    dailySubMode={dailySubMode}
                    onDailySubModeChange={onDailySubModeChange}
                    selectedDate={selectedDate}
                    onDateChange={onDateChange}
                    rangeFrom={rangeFrom}
                    rangeTo={rangeTo}
                    onRangeChange={onRangeChange}
                    defaultWeekKey={defaultWeekKey}
                    selectionScope={`${clientId}-${locationId}`}
                    disabled={isReportLoading}
                />
            )}

            <div className={styles.weeklyLiceReportContainer}>
                {showSkeleton && <LiceReportSkeleton />}
                {!showSkeleton &&
                    selectedReports
                        ?.filter((report) => report !== undefined)
                        .map((report, reportIndex) => {
                            const { isoWeek, locations } = report;
                            const locationsForDisplay = locations.map((location) => ({
                                ...location,
                                cages:
                                    mode === 'daily'
                                        ? mapLiceReportCagesByDataSource(
                                              location.cages,
                                              dataSourceType
                                          )
                                        : location.cages,
                            }));
                            const reportPeriodLabel =
                                mode === 'daily'
                                    ? getDailyLiceReportPeriodLabel(
                                          dailySubMode,
                                          selectedDate,
                                          rangeFrom,
                                          rangeTo
                                      )
                                    : prepareTitle(report);
                            const { allData: pdfAllData } = RegionPreparePDFdata({
                                locationsIncluded: locationsForDisplay as LocationsIncluded[],
                                clientName,
                                reportPeriod: reportPeriodLabel,
                                locale: i18n.language,
                            });
                            const { formattedDataColumnNames } = RegionPreparePDFdata({
                                locationsIncluded: locationsForDisplay as LocationsIncluded[],
                                clientName,
                                reportPeriod: reportPeriodLabel,
                                locale: 'en',
                            });
                            return (
                                <ContentBlock
                                    key={
                                        report.from && report.to
                                            ? `${report.from}-${report.to}`
                                            : report.day
                                              ? report.day
                                              : isoWeek.toString()
                                    }
                                    spacing="none"
                                    title={reportPeriodLabel}
                                    headerActions={
                                        <ExportDataDropDown>
                                            <DownloadOptions
                                                data={pdfAllData}
                                                columns={mapLiceReportExportColumns(
                                                    formattedDataColumnNames,
                                                    tLiceReport
                                                )}
                                                nameToInclude={getLiceReportPrefix(report)}
                                                pdfButtonLocation={false}
                                                pdfButtonRegion={true}
                                                clientName={clientName}
                                                reportPeriodLabel={reportPeriodLabel}
                                            />
                                        </ExportDataDropDown>
                                    }>
                                    {locationsForDisplay?.map((location) => (
                                        <LiceReportLocationTable
                                            reportIndex={reportIndex}
                                            clientId={clientId}
                                            locationId={location.id}
                                            isChileanLice={isChileanLice}
                                            key={location.id}
                                            cages={
                                                location.cages as LiceReportLocationTableProps['cages']
                                            }
                                            title={location.name}
                                            openDefault={true}
                                            onHandleCageSelected={handleCageSelected}
                                            averages={calculateAverage({
                                                cages: location.cages,
                                            })}
                                            mode={mode}
                                        />
                                    ))}
                                </ContentBlock>
                            );
                        })}
                {!showSkeleton && noData && (
                    <ContentBlock spacing="none">
                        <EmptyState
                            size="spacious"
                            live="polite"
                            title={t('common.labels.noDataToDisplay')}
                        />
                    </ContentBlock>
                )}
            </div>
            {notice}
        </PageLayout>
    );
}
